import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterBar, { type FilterGroup } from "@/components/feature/FilterBar";
import AnimeCard from "@/components/feature/AnimeCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { animeList } from "@/mocks/anime";
import {
  genreOptions,
  getCategoryMeta,
  regionOptions,
  yearOptions,
} from "@/mocks/categories";
import { filterAnime } from "@/utils/content";

const PER_PAGE = 8;

const allTypes = ["最新", "热门", "推荐", "资讯"];

export default function Category() {
  const { type = "最新" } = useParams();
  const meta = getCategoryMeta(type);

  const [region, setRegion] = useState("全部");
  const [year, setYear] = useState("全部");
  const [genre, setGenre] = useState("全部");
  const [sort, setSort] = useState("最新");
  const [page, setPage] = useState(1);

  usePageMeta({
    title: meta.title,
    description: meta.description,
    keywords: meta.keywords,
  });

  useEffect(() => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setSort(type === "热门" ? "最热" : "最新");
    setPage(1);
  }, [type]);

  useEffect(() => {
    setPage(1);
  }, [region, year, genre, sort]);

  const baseList = useMemo(() => {
    if (type === "推荐") {
      return animeList.filter((item) =>
        ["治愈", "奇幻", "科幻", "剧情", "日常"].some((tag) => item.tags.includes(tag)),
      );
    }
    if (type === "资讯") {
      return [...animeList].sort((a, b) => b.newsIds.length - a.newsIds.length);
    }
    return animeList;
  }, [type]);

  const filtered = useMemo(
    () =>
      filterAnime(baseList, {
        region,
        year,
        genre,
        sort: sort === "最热" ? "hot" : "latest",
      }),
    [baseList, region, year, genre, sort],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const resetFilters = () => {
    setRegion("全部");
    setYear("全部");
    setGenre("全部");
    setSort(type === "热门" ? "最热" : "最新");
    setPage(1);
  };

  const groups: FilterGroup[] = [
    { label: "地区", options: regionOptions, value: region, onChange: setRegion },
    { label: "年份", options: yearOptions, value: year, onChange: setYear },
    { label: "类型", options: genreOptions, value: genre, onChange: setGenre },
    { label: "排序", options: ["最新", "最热"], value: sort, onChange: setSort },
  ];

  const hasFilter = region !== "全部" || year !== "全部" || genre !== "全部" || sort !== (type === "热门" ? "最热" : "最新");

  return (
    <div className="mx-auto w-full max-w-[1280px] px-4 py-6 md:px-8 md:py-8 xl:px-12">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "动漫分类" }, { label: `${meta.name}动漫` }]} />

      <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-extrabold text-foreground-950 md:text-3xl">
            {meta.name}动漫作品
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-relaxed text-foreground-600">{meta.intro}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {allTypes.map((item) => (
            <Link
              key={item}
              to={`/category/${item}`}
              className={`whitespace-nowrap rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                item === type
                  ? "bg-primary-500 text-background-50"
                  : "border border-background-200 bg-background-50 text-foreground-600 hover:border-primary-300 hover:text-primary-600"
              }`}
            >
              {item}
            </Link>
          ))}
        </div>
      </div>

      <FilterBar
        groups={groups}
        className="mt-6"
        extra={
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="text-xs text-foreground-500">
              共找到 <strong className="text-primary-600">{filtered.length}</strong> 部作品
            </span>
            {hasFilter && (
              <button
                type="button"
                onClick={resetFilters}
                className="flex items-center gap-1 whitespace-nowrap text-xs font-medium text-foreground-600 transition-colors hover:text-primary-600"
              >
                <i className="ri-refresh-line text-sm" />
                重置筛选
              </button>
            )}
          </div>
        }
      />

      <div className="mt-6">
        {paged.length > 0 ? (
          <>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {paged.map((item) => (
                <AnimeCard key={item.id} anime={item} />
              ))}
            </div>
            <Pagination page={page} totalPages={totalPages} onChange={setPage} />
          </>
        ) : (
          <EmptyState
            icon="ri-search-eye-line"
            title="没有找到符合条件的作品"
            description="换个筛选条件试试，或者重置筛选查看全部作品。"
            actionLabel="重置筛选"
            onAction={resetFilters}
          />
        )}
      </div>
    </div>
  );
}